import { isCandidateAuthFailureInline } from 'candidate-app/authFailure';
import { buildPath, paths } from 'url-registry';
import { isRecord } from 'utils/typeGuards';
import { isSharedPagePath } from 'utils/sharedPagePath';
import { StorageKey, storage } from 'utils/typedStorage';

import { hasHeader, readHeader } from './httpHeaders';
export { hasHeader } from './httpHeaders';

// Auth pages are never the page to return to after login: a single-use link
// token, the login form itself, or the no-access screen. Read at call time, not
// module load, like the other `paths` uses here.
const isNoReturnPath = (pathname: string): boolean =>
  pathname === paths.LOGIN ||
  pathname === paths.AUTHENTICATE ||
  pathname === paths.RESTRICTED_ACCESS;

/**
 * Header carrying a share-link session capability (server `SHARE_CAPABILITY_HEADER`). Set only
 * by the shared report route's interceptor (`share-links/shareSession.ts`).
 */
export const SHARE_CAPABILITY_HEADER = 'x-share-capability';

/**
 * Header a request sets to opt out of the global auth redirect: its caller renders the 401 or
 * 403 inline (background pollers, panels holding unsaved work).
 */
export const HANDLES_AUTH_INLINE_HEADER = 'x-handles-auth-inline';
/** Request headers that opt a request out of the global auth redirect. */
export const HANDLES_AUTH_INLINE_HEADERS = {
  [HANDLES_AUTH_INLINE_HEADER]: 'true',
} as const;

/** Handle permission/policy 403s inline while preserving expired/deactivated-session redirects. */
export const HANDLES_FORBIDDEN_INLINE_HEADERS = {
  [HANDLES_AUTH_INLINE_HEADER]: 'forbidden',
} as const;

/** Backend error code on every request once an admin or directory sync turns the user off. */
export const DEACTIVATED_ACCOUNT_CODE = 'LOGIN_ACCOUNT_DEACTIVATED';
/** Query param the login page reads to open straight on the matching card. */
export const LOGIN_REASON_PARAM = 'reason';
export const LOGIN_REASON_DEACTIVATED = 'deactivated';

/** Apply the same session redirect policy to Axios and fetch responses. */
export function handleAuthFailure(
  response: { status: number; data: unknown } | undefined,
  headers: unknown
): void {
  if (typeof window === 'undefined') return;
  const body = isRecord(response?.data) ? response.data : undefined;
  // Google Meet add-on requests run inside the Meet side-panel iframe and
  // candidate routes live in InterviewApp — both self-authenticate (Meet via
  // its own GIS token, candidates via path-scoped tokens) and have no Tenzo
  // session. For them an auth failure must bubble back so the page renders the
  // real reason inline; a hard redirect to /login or /restricted-access breaks
  // the iframe and bounces candidates/users to the staff sign-up page.
  const isGoogleMeetAuth = hasHeader(headers, 'google-meet-auth-token');
  // Teams side-panel requests likewise self-authenticate (synthetic
  // tenant|oid token); the page probes candidate tenants and must see auth
  // failures inline instead of a redirect.
  const isTeamsAuth = hasHeader(headers, 'microsoft-teams-auth-token');
  // The Zoom in-meeting app holds its session in memory; a redirect would drop it.
  const isZoomAuth = hasHeader(headers, 'zoom-session-token');
  // A share-link viewer's capability authorizes the request, not a Tenzo session: its 401
  // (an expired or revoked link) and 403 (outside the link's scope) are rendered by the shared
  // page, and a redirect would send someone without an account to the staff login.
  const isShareCapabilityRequest = hasHeader(headers, SHARE_CAPABILITY_HEADER);
  const isCandidateFacingApp = isCandidateAuthFailureInline(
    window.location.pathname
  );
  // Background pollers opt out explicitly: a redirect away from the page would discard
  // unsaved work over a request the user never made, so they handle the failure themselves.
  const inlineAuthMode =
    headers instanceof Headers
      ? headers.get(HANDLES_AUTH_INLINE_HEADER)
      : readHeader(headers, HANDLES_AUTH_INLINE_HEADER);
  const handlesForbiddenInline = inlineAuthMode === 'forbidden';
  // Preserve the presence-based opt-out for existing callers; only the explicit
  // forbidden mode lets session expiry/deactivation follow the normal login flow.
  const optsOutOfAuthRedirect =
    hasHeader(headers, HANDLES_AUTH_INLINE_HEADER) && !handlesForbiddenInline;
  // A share-link page renders its own states. Its viewer may also hold a stale Tenzo token
  // (the session verify request the app starts on load carries no share capability), and a
  // login redirect would drop the URL fragment that carries the link's secret.
  const isSharedPage = isSharedPagePath(window.location.pathname);
  const handlesAuthInline =
    isSharedPage ||
    isGoogleMeetAuth ||
    isTeamsAuth ||
    isZoomAuth ||
    isCandidateFacingApp ||
    isShareCapabilityRequest ||
    optsOutOfAuthRedirect;
  // A request carrying a share credential comes from a link viewer who may hold no session at
  // all, so its 401 is not session death: clearing storage and redirecting would throw a
  // link-public page out to the staff login page. Only the 401 branch is exempt -- a 403 means
  // the link itself does not grant access, which is meant to reach /restricted-access.
  const isShareLinkRequest = hasHeader(headers, 'transcript-campaignId');

  if (
    response &&
    response.status === 401 &&
    body?.loginRequired &&
    !handlesAuthInline &&
    !isShareLinkRequest
  ) {
    // A protected page can render from the cached session before verify returns,
    // so its own request may be the first to meet an expired token. Keep the page
    // to return to after login: one ProtectedRoute already saved, else this one
    // unless it is an auth page itself.
    const routeAfterLogin =
      storage.get(StorageKey.RouteAfterRedirect) ??
      (isNoReturnPath(window.location.pathname)
        ? null
        : window.location.pathname + window.location.search);
    // Clear all localStorage
    storage.clear();
    if (routeAfterLogin) {
      storage.set(StorageKey.RouteAfterRedirect, routeAfterLogin);
    }
    window.location.href = paths.LOGIN;
  } else if (
    response &&
    response.status === 403 &&
    body?.detailCode === DEACTIVATED_ACCOUNT_CODE &&
    !handlesAuthInline
  ) {
    // The session is dead for good, not short on permissions: /restricted-access
    // would itself be refused and loop. Drop the session and let the login page
    // explain, the same card a deactivated person sees when they try to sign in.
    storage.clear();
    window.location.href = buildPath(paths.LOGIN, undefined, {
      [LOGIN_REASON_PARAM]: LOGIN_REASON_DEACTIVATED,
    });
  } else if (
    response &&
    response.status === 403 &&
    !handlesAuthInline &&
    !handlesForbiddenInline
  ) {
    const attemptedUrl = window.location.pathname + window.location.search;
    window.location.href = buildPath(paths.RESTRICTED_ACCESS, undefined, {
      attemptedUrl,
    });
  }
}
